import React, {memo, useCallback} from 'react'
import {
  Box,
  TextField,
  Button,
  IconButton,
  Theme,
  InputAdornment,
  Typography,
  Fade,
} from '@material-ui/core'
import {makeStyles} from '@material-ui/core/styles'
import MicIcon from '@material-ui/icons/Mic'
import SendIcon from '@material-ui/icons/Send'
import MicOffIcon from '@material-ui/icons/MicOff'

import {Message} from '../types/chat'
import {OPTION_LANGUAGES} from '../utils/questionOptionLanguage'

interface StyleProps {
  isRecording: boolean
  numericalVolume: number
}

const useStyles = makeStyles((theme: Theme) => ({
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.4)',
    },
    '70%': {
      boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)',
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)',
    },
  },
  form: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  sendButton: {
    minWidth: '80px',
  },
  micButton: {
    color: (props: StyleProps) => {
      if (props.isRecording && props.numericalVolume > 0.01)
        return theme.palette.secondary.light
      if (props.isRecording) return theme.palette.secondary.dark
      return theme.palette.primary.main
    },
    transform: (props: StyleProps) => {
      const baseScale = props.isRecording ? 3 : 1.0
      const volumeScale = props.isRecording
        ? Math.min(1 + props.numericalVolume * 6, 2)
        : 1.0
      return `scale(${baseScale * volumeScale})`
    },
    transition: 'color 0.1s ease-in-out, transform 0.15s ease-in-out',
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  textField: {
    marginBottom: theme.spacing(1),
  },
  inputRoot: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: (props: StyleProps) =>
        props.isRecording
          ? theme.palette.secondary.main
          : theme.palette.primary.main,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: (props: StyleProps) =>
        props.isRecording
          ? theme.palette.secondary.main
          : theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.secondary.main,
    },
  },
  inputFocused: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.secondary.main,
    },
  },
  interimContainer: {
    position: 'relative',
    marginTop: '-36px',
    marginBottom: '12px',
    minHeight: '24px',
  },
  interimText: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    fontSize: '0.875rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  optionsContainer: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap',
    justifyContent: 'center',
    paddingBottom: '3px',
  },
  optionButton: {
    textTransform: 'none',
  },
}))

/**
 * Props for the MessageInput component
 */
interface MessageInputProps {
  /** Current value of the input field */
  value: string
  /** Callback for when the input value changes */
  onChange: (value: string) => void
  /** Callback for when the message should be sent */
  onSend: () => void
  /** Whether audio recording is currently active */
  isRecording: boolean
  /** Callback to start audio recording */
  onStartRecording: () => void
  /** Callback to stop audio recording */
  onStopRecording: () => void
  /** The interim transcript from real-time speech recognition */
  interimTranscript?: string
  /** Whether the component is disabled */
  isDisabled?: boolean
  /** Whether the mic button itself should be disabled (independent of general isDisabled for text field) */
  isMicButtonDisabled?: boolean
  /** Numerical volume level (e.g., 0.0 to 1.0) */
  numericalVolume?: number

  currentField?: Message | null

  selectedLanguageCode?: string | null

  onOptionClick: (option: string) => void

  knowMoreClick: () => void
}

/**
 * Component that provides the input controls for the chat interface
 * Includes text input, voice recording, and send button
 */
const MessageInput: React.FC<MessageInputProps> = memo(
  ({
    value,
    onChange,
    onSend,
    isRecording,
    onStartRecording,
    onStopRecording,
    interimTranscript = '',
    isDisabled = false,
    isMicButtonDisabled = false,
    numericalVolume = 0,
    currentField,
    selectedLanguageCode,
    onOptionClick,
    knowMoreClick,
  }) => {
    const classes = useStyles({isRecording, numericalVolume})

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        if (value.trim() && !isDisabled) {
          onSend()
        }
      },
      [value, isDisabled, onSend]
    )

    const getOptionText = (
      selectedOptionName: string,
      selectedLanguageCode: string | null
    ): string => {
      const optionConfig = OPTION_LANGUAGES.find(
        (config) => config.option_name === selectedOptionName
      )

      if (!optionConfig) {
        return selectedOptionName
      }

      if (selectedLanguageCode == null) {
        return selectedOptionName
      }

      const getTextForLanguage = (code: string): string | undefined =>
        optionConfig.languages.find((lang) => lang.code === code)?.languageBody
          .option_name

      const localizedText =
        getTextForLanguage(selectedLanguageCode) || getTextForLanguage('en-US')

      if (!localizedText) {
        return selectedOptionName
      }

      if (
        selectedLanguageCode !== 'en-US' &&
        !getTextForLanguage(selectedLanguageCode)
      ) {
        console.warn(
          `[OptionText] Language "${selectedLanguageCode}" not found for "${selectedOptionName}". Falling back to English.`
        )
      }

      return localizedText
    }

    return (
      <Box component="form" onSubmit={handleSubmit}>
        <div className={classes.optionsContainer}>
          {currentField &&
            selectedLanguageCode &&
            !currentField.isUser &&
            currentField.current_field_options &&
            currentField.current_field_options.length > 0 && (
              <Box className={classes.optionsContainer}>
                {/* <div>{currentField.field}</div> */}
                {currentField.current_field_options.map((option) => {
                  if (!option.trim()) return null
                  const lower = option.toLowerCase()
                  const backgroundColor =
                    lower === 'yes'
                      ? '#427f78' // custom green
                      : lower === 'no'
                      ? '#d32f2f' // red
                      : '#427f78' // default blue (MUI primary)
                  const optionText = getOptionText(option, selectedLanguageCode)

                  return (
                    <Button
                      key={option}
                      variant="contained"
                      className={classes.optionButton}
                      disabled={isMicButtonDisabled}
                      onClick={() => {
                        onOptionClick(optionText)
                      }}
                      style={{
                        backgroundColor,
                        color: '#fff',
                      }}
                    >
                      {optionText}
                    </Button>
                  )
                })}
                <Button
                  aria-label="Know more about this question?"
                  onClick={knowMoreClick}
                  style={{
                    // color: '#3F51B5',
                    color: 'white',
                    textTransform: 'none',
                    minWidth: 'auto',
                    padding: 4,
                    backgroundColor: '#227ac2',
                  }}
                >
                  {getOptionText('Why?', selectedLanguageCode ?? null)}
                </Button>
              </Box>
            )}
          {/* <IconButton
            aria-label="Know more about this question?"
            onClick={() => {
              knowMoreClick()
            }}
          >
            <HelpOutlineIcon style={{color: '#3F51B5'}} />
          </IconButton> */}
          {/* <Box className={classes.optionsContainer}>
            <Button
              aria-label="Know more about this question?"
              onClick={knowMoreClick}
              style={{
                // color: '#3F51B5',
                color: 'white',
                textTransform: 'none',
                minWidth: 'auto',
                padding: 4,
                backgroundColor: '#227ac2',
              }}
            >
              {getOptionText('Why?', selectedLanguageCode ?? null)}
            </Button>
          </Box> */}
        </div>
        <div className={classes.inputContainer}>
          <Box display="flex" flexDirection="row" alignItems="center">
            <TextField
              placeholder={
                isRecording ? 'Listening...' : 'Type your message...'
              }
              // maxRows={4}
              fullWidth
              value={value}
              onChange={(e) => onChange(e.target.value)}
              disabled={isDisabled}
              className={classes.textField}
              variant="outlined"
              InputProps={{
                classes: {
                  root: classes.inputRoot,
                  focused: classes.inputFocused,
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      className={classes.micButton}
                      onClick={isRecording ? onStopRecording : onStartRecording}
                      disabled={isMicButtonDisabled}
                      color={isRecording ? 'default' : 'secondary'}
                      aria-label={
                        isRecording ? 'Stop recording' : 'Start recording'
                      }
                    >
                      {isRecording ? <MicIcon /> : <MicOffIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              multiline
            />
          </Box>

          {isRecording && (
            <div className={classes.interimContainer}>
              <Fade in={!!interimTranscript}>
                <Typography className={classes.interimText}>
                  {interimTranscript || ''}
                </Typography>
              </Fade>
            </div>
          )}

          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={isDisabled || value.trim() === ''}
            className={classes.sendButton}
          >
            <SendIcon />
          </Button>
        </div>
      </Box>
    )
  }
)

MessageInput.displayName = 'MessageInput'

export default MessageInput
